import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import EmailTrackingBadge from './Email/EmailTrackingBadge';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  Divider,
  useColorModeValue,
  Icon,
  Tooltip,
  IconButton,
  useToast,
  Collapse,
  Button,
} from '@chakra-ui/react';
import {
  FiPhone,
  FiMail,
  FiMessageSquare,
  FiEdit2,
  FiCheckCircle,
  FiAlertCircle,
  FiUser,
  FiCalendar,
  FiAlertTriangle,
  FiCopy,
  FiChevronDown,
  FiChevronUp,
  FiXCircle,
  FiUserX,
  FiPrinter,
  FiFileText,
} from 'react-icons/fi';
import { DISPOSITIONS, STAGES } from '../../constants/crmConstants';
import moment from 'moment';

/**
 * Strip HTML tags from content to create plain text version
 */
function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const ACTIVITY_ICONS = {
  disposition: FiPhone,
  stage_change: FiCheckCircle,
  edit: FiEdit2,
  update: FiAlertCircle,
  created: FiUser,
  note: FiFileText,
  call_summary: FiPhone,
  email_sent: FiMail,
  sms_sent: FiMessageSquare,
  meeting_scheduled: FiCalendar,
  meeting_rescheduled: FiCalendar,
  meeting_cancelled: FiXCircle,
  meeting_no_show: FiUserX,
  auto_update: FiEdit2,
  transfer: FiUser,
  conversion: FiCheckCircle,
  dialpad_call: FiPhone,
  dialpad_sms: FiMessageSquare,
  sms_failed: FiAlertTriangle,
  email_failed: FiAlertTriangle,
  postcard_sent: FiPrinter,
  postcard_failed: FiAlertTriangle,
};

const ACTIVITY_COLORS = {
  disposition: 'blue',
  stage_change: 'green',
  edit: 'yellow',
  update: 'purple',
  created: 'cyan',
  note: 'yellow',
  call_summary: 'cyan',
  email_sent: 'purple',
  sms_sent: 'teal',
  meeting_scheduled: 'orange',
  meeting_rescheduled: 'orange',
  meeting_cancelled: 'red',
  meeting_no_show: 'red',
  auto_update: 'gray',
  transfer: 'orange',
  conversion: 'green',
  dialpad_call: 'blue',
  dialpad_sms: 'teal',
  sms_failed: 'red',
  email_failed: 'red',
  postcard_sent: 'green',
  postcard_failed: 'red',
};

function isInboundSmsActivity(activity) {
  const type = activity.activityType || activity.type;
  if (type !== 'dialpad_sms' && type !== 'sms_sent') return false;
  if (activity.direction === 'inbound' || activity.direction === 'incoming') return true;
  const text = activity.text || activity.content || '';
  return /^Inbound SMS/i.test(text);
}

export default function DealTimeline({ activities, maxHeight = '500px' }) {
  const [expandedEmails, setExpandedEmails] = useState({});
  const toast = useToast();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const timelineBG = useColorModeValue('gray.50', 'gray.900');
  const cardBG = useColorModeValue('white', 'gray.700');
  const lineColor = useColorModeValue('gray.300', 'gray.600');
  const emailBodyBg = useColorModeValue('white', 'gray.600');

  // Toggle email body expansion
  const toggleEmailExpanded = (activityId) => {
    setExpandedEmails(prev => ({
      ...prev,
      [activityId]: !prev[activityId],
    }));
  };

  // Copy email to clipboard
  const copyEmailToClipboard = (activity) => {
    const subject = activity.subject || '(No Subject)';
    const body = stripHtml(activity.body || '');
    const to = activity.to || '';
    
    const formattedEmail = `Subject: ${subject}\nTo: ${to}\n\n${body}`;
    
    navigator.clipboard.writeText(formattedEmail);
    toast({
      title: 'Copied to clipboard',
      description: 'Email content copied successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  if (!activities || activities.length === 0) {
    return (
      <Box textAlign="center" py={12}>
        <Icon as={FiMessageSquare} boxSize={12} color="gray.300" mb={3} />
        <Text color="gray.500">No activity yet</Text>
        <Text fontSize="sm" color="gray.400">
          Be the first to log an interaction
        </Text>
      </Box>
    );
  }

  // Group activities by date
  const groupedActivities = activities.reduce((groups, activity) => {
    const timestamp = activity.timestamp?.seconds 
      ? moment(activity.timestamp.seconds * 1000)
      : moment(activity.timestamp);
    
    const dateKey = timestamp.format('YYYY-MM-DD');
    
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    
    groups[dateKey].push(activity);
    return groups;
  }, {});

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const time = timestamp.seconds 
      ? moment(timestamp.seconds * 1000)
      : moment(timestamp);
    return time.format('h:mm A');
  };

  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const time = timestamp.seconds 
      ? moment(timestamp.seconds * 1000)
      : moment(timestamp);
    return time.fromNow();
  };

  const getActivityIcon = (activity) => {
    const activityType = activity.activityType || activity.type || 'update';
    return ACTIVITY_ICONS[activityType] || FiAlertCircle;
  };

  const getActivityColor = (activity) => {
    const activityType = activity.activityType || activity.type || 'update';
    return ACTIVITY_COLORS[activityType] || 'gray';
  };

  return (
    <Box
      bg={timelineBG}
      p={4}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      maxH={maxHeight}
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '4px' },
        '&::-webkit-scrollbar-thumb:hover': { background: '#A0AEC0' },
      }}
    >
      <VStack align="stretch" spacing={6}>
        {Object.entries(groupedActivities).sort(([dateA], [dateB]) => dateB.localeCompare(dateA)).map(([dateKey, dateActivities]) => {
          const date = moment(dateKey);
          const isToday = date.isSame(moment(), 'day');
          const isYesterday = date.isSame(moment().subtract(1, 'day'), 'day');

          let dateLabel = date.format('MMM DD, YYYY');
          if (isToday) dateLabel = 'Today';
          else if (isYesterday) dateLabel = 'Yesterday';

          return (
            <Box key={dateKey}>
              {/* Date Header */}
              <HStack mb={4}>
                <Divider flex={1} borderColor={lineColor} />
                <Badge
                  colorScheme={isToday ? 'blue' : 'gray'}
                  fontSize="sm"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {dateLabel}
                </Badge>
                <Divider flex={1} borderColor={lineColor} />
              </HStack>

              {/* Timeline Items */}
              <VStack align="stretch" spacing={3} position="relative" pl={6}>
                {/* Vertical Line */}
                <Box
                  position="absolute"
                  left="17px"
                  top="0"
                  bottom="0"
                  width="2px"
                  bg={lineColor}
                />

                {dateActivities.map((activity, index) => {
                  const ActivityIcon = getActivityIcon(activity);
                  const activityColor = getActivityColor(activity);

                  return (
                    <HStack key={activity.id} align="flex-start" spacing={0} position="relative">
                      {/* Timeline Icon */}
                      <Box
                        position="absolute"
                        left="-26px"
                        top="0"
                        zIndex={1}
                      >
                        <Tooltip label={activity.activityType || activity.type || 'Activity'}>
                          <Box
                            bg={`${activityColor}.100`}
                            borderWidth="2px"
                            borderColor={`${activityColor}.500`}
                            borderRadius="full"
                            p={2}
                          >
                            <Icon
                              as={ActivityIcon}
                              boxSize={4}
                              color={`${activityColor}.600`}
                            />
                          </Box>
                        </Tooltip>
                      </Box>

                      {/* Activity Card */}
                      <Box
                        flex={1}
                        bg={cardBG}
                        p={3}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={borderColor}
                        shadow="sm"
                        transition="all 0.2s"
                        _hover={{ shadow: 'md', borderColor: `${activityColor}.300` }}
                      >
                        <VStack align="stretch" spacing={2}>
                          {/* Header */}
                          <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2} minW={0}>
                            <HStack spacing={2} flex="1 1 auto" minW={0}>
                              <Avatar
                                size="xs"
                                name={activity.authorName}
                                src={activity.authorPhotoURL}
                                flexShrink={0}
                              />
                              <VStack align="flex-start" spacing={0} minW={0}>
                                <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                                  {activity.authorName || 'Unknown'}
                                </Text>
                                {activity.disposition && (
                                  <Badge colorScheme="blue" fontSize="xs">
                                    {DISPOSITIONS.find((d) => d.value === activity.disposition)?.label ||
                                      activity.disposition}
                                  </Badge>
                                )}
                                {isInboundSmsActivity(activity) && (
                                  <Badge colorScheme="purple" fontSize="xs">Received</Badge>
                                )}
                              </VStack>
                            </HStack>
                            
                            <VStack align="flex-end" spacing={0} flexShrink={0}>
                              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                {formatTimestamp(activity.timestamp)}
                              </Text>
                              <Text fontSize="2xs" color="gray.400">
                                {getRelativeTime(activity.timestamp)}
                              </Text>
                            </VStack>
                          </HStack>

                          {/* Activity Text */}
                          {activity.text && (
                            <Text fontSize="sm" whiteSpace="pre-wrap">
                              {activity.text}
                            </Text>
                          )}

                          {activity.description && !activity.text && (
                            <Text fontSize="sm" color="gray.600">
                              {activity.description}
                            </Text>
                          )}

                          {/* Changes List */}
                          {activity.changes && activity.changes.length > 0 && (
                            <VStack align="stretch" spacing={1} mt={1}>
                              {activity.changes.map((change, idx) => (
                                <HStack key={idx} fontSize="xs" color="gray.600" pl={2} align="flex-start" minW={0}>
                                  <Box
                                    w="4px"
                                    h="4px"
                                    mt="6px"
                                    flexShrink={0}
                                    borderRadius="full"
                                    bg={`${activityColor}.400`}
                                  />
                                  <Text minW={0} wordBreak="break-word">{change}</Text>
                                </HStack>
                              ))}
                            </VStack>
                          )}

                          {/* Email Activity Details */}
                          {(activity.activityType === 'email_sent' || activity.type === 'email_sent') && (
                            <Box 
                              mt={2} 
                              p={3} 
                              bg="purple.50" 
                              borderRadius="md" 
                              borderLeftWidth="3px" 
                              borderLeftColor="purple.400"
                            >
                              <VStack align="stretch" spacing={2}>
                                {/* Email Header */}
                                <HStack justify="space-between" align="flex-start" gap={2} minW={0}>
                                  <VStack align="stretch" spacing={1} flex="1 1 0" minW={0}>
                                    {activity.subject && (
                                      <HStack spacing={2} minW={0}>
                                        <Icon as={FiMail} boxSize={3} color="purple.500" flexShrink={0} />
                                        <Text fontSize="sm" fontWeight="semibold" color="purple.800" noOfLines={2} minW={0}>
                                          {activity.subject}
                                        </Text>
                                      </HStack>
                                    )}
                                    {activity.to && (
                                      <Text fontSize="xs" color="gray.600" pl={5} wordBreak="break-all">
                                        To: {activity.to}
                                      </Text>
                                    )}
                                    {activity.sentAsEmail && (
                                      <Badge colorScheme="orange" fontSize="2xs" alignSelf="flex-start" ml={5}>
                                        Sent as: {activity.sentAsUserName || activity.sentAsEmail}
                                      </Badge>
                                    )}
                                  </VStack>
                                  
                                  {/* Copy Button */}
                                  <Tooltip label="Copy email to clipboard">
                                    <IconButton
                                      icon={<FiCopy />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="purple"
                                      aria-label="Copy email"
                                      flexShrink={0}
                                      onClick={() => copyEmailToClipboard(activity)}
                                    />
                                  </Tooltip>
                                </HStack>

                                <EmailTrackingBadge activityId={activity.id} />

                                {activity.body && (
                                  <>
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="purple"
                                      leftIcon={expandedEmails[activity.id] ? <FiChevronUp /> : <FiChevronDown />}
                                      onClick={() => toggleEmailExpanded(activity.id)}
                                      alignSelf="flex-start"
                                    >
                                      {expandedEmails[activity.id] ? 'Hide email body' : 'Show email body'}
                                    </Button>
                                    
                                    <Collapse in={expandedEmails[activity.id]} animateOpacity>
                                      <Box
                                        mt={2}
                                        p={3}
                                        bg={emailBodyBg}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor="purple.200"
                                        maxH="300px"
                                        overflowY="auto"
                                        overflowX="auto"
                                        css={{
                                          '&::-webkit-scrollbar': { width: '6px' },
                                          '&::-webkit-scrollbar-thumb': { background: '#9F7AEA', borderRadius: '3px' },
                                        }}
                                      >
                                        {/* Inbound marketing HTML can carry fixed-width tables and
                                            oversized images; constrain them so they scroll here
                                            rather than widening the page. */}
                                        <Text 
                                          fontSize="sm" 
                                          whiteSpace="pre-wrap"
                                          color="gray.700"
                                          wordBreak="break-word"
                                          sx={{
                                            '& img': { maxWidth: '100%', height: 'auto' },
                                            '& table': { maxWidth: '100%' },
                                            '& pre': { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
                                          }}
                                          dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(activity.body || '')
                                          }}
                                        />
                                      </Box>
                                    </Collapse>
                                  </>
                                )}
                              </VStack>
                            </Box>
                          )}

                          {/* Stage Change Indicator */}
                          {activity.activityType === 'stage_change' && activity.previousStage && activity.newStage && (
                            <HStack spacing={2} mt={2} fontSize="sm" flexWrap="wrap" gap={2} minW={0}>
                              <Badge colorScheme="gray" whiteSpace="normal">
                                {STAGES.find(s => s.value === activity.previousStage)?.label || activity.previousStage}
                              </Badge>
                              <Text>→</Text>
                              <Badge colorScheme="green" whiteSpace="normal">
                                {STAGES.find(s => s.value === activity.newStage)?.label || activity.newStage}
                              </Badge>
                            </HStack>
                          )}
                        </VStack>
                      </Box>
                    </HStack>
                  );
                })}
              </VStack>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}

